import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import {
  Menu,
  CopyPermissionRequest,
  MemberMenuPermission,
  ApiResponse,
  MyMenuSaveRequest,
  MyMenuResponse,
} from "@/interface";
import { toast } from "@/lib/toast";

const menuQueryKey = "menu";

export const useMenuList = () => {
  return useQuery<Menu[]>({
    queryKey: [menuQueryKey],
    queryFn: () => api.get("/menu"),
    select: (data: Menu[]) => data,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useMenuChildrenList = () => {
  return useQuery<Menu[]>({
    queryKey: [menuQueryKey, "children"],
    queryFn: () => api.get("/menu/children"),
    select: (data: Menu[]) => data,
  });
};

export const useUserMenuList = () => {
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      const response = await api.post("/menu/member", null, { params: { loginName: data.name } });
      return response;
    },
    onSuccess: res => {
      // console.log(res);
    },
    onError: err => {
      console.error(err);
      toast.error("메뉴 조회 실패");
    },
  });
};

export const useMyMenuList = (loginName: string) => {
  return useQuery<MyMenuResponse>({
    queryKey: [menuQueryKey, "my", loginName],
    queryFn: () => {
      return api.get(`/menu/my?loginName=${loginName}`);
    },
    select: (data: MyMenuResponse) => data,
    enabled: !!loginName, // loginName이 있을 때만 호출
  });
};

export const useMyMenuSave = () => {
  return useMutation({
    mutationFn: async (data: MyMenuSaveRequest) => {
      const response = await api.post("/menu/my/save", data);
      return response;
    },
    onSuccess: (res: ApiResponse) => {
      console.log(res);
      if (res.code === 200) {
        toast.success("마이메뉴가 저장되었습니다.");
      } else {
        toast.error(res.message);
      }
    },
    onError: err => {
      console.error(err);
      toast.error("마이메뉴 저장 실패");
    },
  });
};

export const useSaveMenuPermission = () => {
  return useMutation({
    mutationFn: async (data: { memberName: string; menuId: number; permissions: Record<string, boolean> }) => {
      const response = await api.post("/menu/permission", data.permissions, {
        params: {
          memberName: data.memberName,
          menuId: data.menuId,
        },
      });
      return response;
    },
    onSuccess: res => {
      toast.success("권한이 성공적으로 저장되었습니다.");
    },
    onError: err => {
      console.error(err);
      toast.error("권한 저장 실패");
    },
  });
};

export const useCopyPermission = () => {
  return useMutation({
    mutationFn: async (data: CopyPermissionRequest) => {
      const response = await api.post("/menu/permission/copy", data);
      return response;
    },
    onSuccess: res => {
      console.log(res);
      toast.success("권한 복사가 완료되었습니다.");
    },
    onError: err => {
      console.error(err);
      toast.error("권한 복사 실패");
    },
  });
};
